<template>
  <div class="dropdown-range">
    <div
      v-if="isDropdownOpen"
      class="dropdown-range__overlay"
      @click="isDropdownOpen = false" />

    <div
      class="dropdown-range__selector"
      :class="{
        'dropdown-range__selector--active': isDropdownOpen,
        'dropdown-range__selector--has-value': isValuesSet,
      }"
      @click="isDropdownOpen = !isDropdownOpen">
      <input
        class="dropdown-range__input"
        :placeholder="placeholder">

      <div class="dropdown-range__icons">
        <div
          v-if="isValuesSet"
          class="dropdown-range__cross shape-cross"
          @click.stop="clearValues" />

        <arrowhead-icon
          class="dropdown-range__arrow"
          :is-reverse="isDropdownOpen" />
      </div>
    </div>

    <transition
      name="transition--opacity"
      mode="out-in">
      <div
        v-if="isDropdownOpen"
        class="dropdown-range__dropdown">
        <div class="dropdown-range__dropdown-items">
          <div class="dropdown-range__dropdown-item">
            <div class="dropdown-range__dropdown-item-title">
              From
            </div>

            <ui-input
              :value="currentValues[0]"
              inputmode="number"
              type="number"
              :min="range.borderValues[0]"
              :max="range.borderValues[1]"
              class="dropdown-range__dropdown-item-input"
              @input="setInputRangeValues(0, Number($event))" />
          </div>

          <div class="dropdown-range__dropdown-item">
            <div class="dropdown-range__dropdown-item-title">
              To
            </div>

            <ui-input
              :value="currentValues[1]"
              inputmode="number"
              type="number"
              :min="range.borderValues[0]"
              :max="range.borderValues[1]"
              class="dropdown-range__dropdown-item-input"
              @input="setInputRangeValues(1, Number($event))" />
          </div>
        </div>

        <ui-range
          v-model="currentValues"
          :zones="range.zones"
          :show-scale="true"
          :scale-steps-count="range.scaleStepsCount || 4"
          class="dropdown-range__range" />
      </div>
    </transition>
  </div>
</template>

<script>
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import UiRange from 'Components/UiRange.vue';
import UiInput from 'Components/ui/Input/UiInput.vue';
import { _debounce as debounce } from 'Root/helpers/tools';

export default {
  name: 'DropdownRange',
  components: {
    UiInput,
    UiRange,
    ArrowheadIcon,
  },

  props: {
    /*
     * Объект с данными для дропдауна
     * {
     *   values: [Number, Number],
     *   borderValues: [Number, Number],
     *   step: Number,
     *   zones: [
     *     {
     *       from: 0, // с какого значения зона действует
     *       to: 20, // до какого значения зона действует
     *       size: 50, // процент "занимаемого" зоной места
     *       step: 1, // размер шага значений в этой зоне
     *     }
     *   ],
     *   scaleStepsCount: Number,
     * }
     */
    range: {
      type: Object,
      required: true,
      validate: (range) => range.values && range.borderValues,
    },

    placeholder: {
      type: String,
      default: '',
    },

    values: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      currentValues: this.values,
      isDropdownOpen: false,
      isValuesSet: false,
    };
  },

  watch: {
    range: {
      handler() {
        this.currentValues = this.range.values;
      },
      immediate: true,
      deep: true,
    },

    currentValues: {
      handler(newValue, oldValue) {
        if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
          return;
        }

        this.$emit('input', this.currentValues);
        this.isValuesSet = JSON.stringify(this.currentValues) !== JSON.stringify(this.range.values)
          && JSON.stringify(this.currentValues) !== JSON.stringify(this.range.borderValues);
      },
      deep: true,
    },
  },

  created() {
    this.isValuesSet = JSON.stringify(this.currentValues) !== JSON.stringify(this.range.borderValues);

    if (this.isValuesSet) {
      this.$emit('input', this.currentValues);
    }
  },

  methods: {
    // eslint-disable-next-line func-names
    setInputRangeValues: debounce(function (index, value) {
      if (value === this.currentValues[index]) {
        return;
      }

      let newValue = value;
      let currentStep = this.range.step;
      this.$set(this.currentValues, index, null);

      for (let i = 0; i < this.range.zones.length; i++) {
        const zone = this.range.zones[i];
        if (value >= zone.from && value <= zone.to) {
          currentStep = zone.step;
          break;
        }
      }

      if (!index && value >= this.currentValues[1]) { /* index === 0 */
        newValue = this.currentValues[1] - currentStep;
      } else if (index && value <= this.currentValues[0]) { /* index === 1 */
        newValue = this.currentValues[0] + currentStep;
      } else {
        // округляем до ближайшего шага
        newValue = Math.round(value / currentStep) * currentStep;
      }

      this.$nextTick(() => {
        this.$set(this.currentValues, index, newValue);
        this.$emit('input', this.currentValues);
      });
    }, 1000),

    clearValues() {
      this.currentValues = JSON.parse(JSON.stringify(this.range.borderValues));
    },
  },
};
</script>

<style scoped lang="scss">
.dropdown-range {
  position: relative;

  --border: 1px solid #{rgba($color--basic-blue, 0.25)};
  --border-radius: 4px;

  --input-bg-color: #{rgba($color--basic-blue, 0.25)};
  --input-bg-color-active: #{rgba($color--basic-blue, 0.45)};
  --input-height: 32px;
  --input-padding: 9px 12px;
  --input-color: $color--basic-white;
  --input-font-size: 13px;
  --input-font-weight: 500;

  // Не стал делать прозрачный фон, как в макете, ибо это выглядит всрато.
  --dropdown-bg-color: #303c70;
  --dropdown-padding: 8px 12px;
}

.dropdown-range__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 17;
}

.dropdown-range__selector {
  position: relative;
  display: flex;
  align-items: center;
  height: var(--input-height);
  padding: var(--input-padding);
  background-color: var(--input-bg-color);
  border: var(--border);
  border-radius: var(--border-radius);
  cursor: pointer;
  z-index: 16;
  transition: 0.2s;

  &--active {
    z-index: 18;
  }

  &--has-value {
      background-color: var(--input-bg-color-active);
  }
}

.dropdown-range__input {
  border: none;
  background: transparent;
  pointer-events: none;
  font-family: Rubik, sans-serif;
  font-size: var(--input-font-size);
  font-weight: var(--input-font-weight);
  color: var(--input-color);

  &::placeholder {
    color: var(--input-color);
  }
}

.dropdown-range__icons {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  gap: 12px;
}

.dropdown-range__cross,
.dropdown-range__arrow {
  width: 16px;
  height: 16px;
}

.dropdown-range__dropdown {
  position: absolute;
  z-index: 17;
  width: 100%;
  padding: var(--dropdown-padding, 4px);
  border-radius: var(--border-radius);
  border: var(--border, none);
  background: var(--dropdown-bg-color);
  transform: translateY(2px);
}

.dropdown-range__dropdown-items {
  display: flex;
  margin-bottom: 16px;
}

.dropdown-range__dropdown-item {
  display: flex;
  flex: 1;
  flex-direction: column;

  &:not(:last-child) {
    margin-right: 10px;
  }
}

.dropdown-range__dropdown-item-title {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: $color--basic-white;
}

.dropdown-range__dropdown-item-input {
  --background-color: var(--input-bg-color);
  --background-color--focused: var(--input-bg-color);
  --border: var(--border);
  --border-radius: var(--border-radius);
  --color: var(--input-color);
  --font-size: var(--input-font-size);
  --font-weight: var(--input-font-weight);
  --height: var(--input-height);
  --padding: var(--input-padding);

  width: 100%;
}

.dropdown-range__range {
  margin: 0 12px;
}
</style>
